import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { cloneDeep } from 'lodash'
import scriptjs from 'scriptjs'

import CkEditorTextbox from '@scoir/components-core/dist/components/CkEditorTextbox'
import CharactersRemaining from '@scoir/components-core/dist/components/CharactersRemaining'

import { TableCell, TableRow, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'

import { ErrorBoundary } from '@scoir/error-reporter'
import SectionTable from './components/SectionTable'

import sanitize from 'common/sanitize-html-helper'
import { getCharCount } from './textUtilities'

const useStyles = makeStyles((theme) => ({
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  exceedsError: {
    color: theme.palette.error.main,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  loadingBox: {
    '& .MuiSkeleton-text': {
      transform: 'unset',
    },
  },
  thirdLevelHeading: {
    color: theme.palette.text.secondary,
    paddingBottom: 30,
  },
}))

const toolbar = [
  {
    name: 'basicstyles',
    items: [],
  },
  {
    name: 'insert',
    items: ['Link', 'Unlink'],
  },
]

const maxNoteLength = 2400
const additionNoteText =
  'Please provide any additional relevant details that will help an applicant understand what they will encounter on your supplement. If you have a page on your website that describes this, please include the link.'

const NotesSection = ({ isEditing, applicationConfig, setApplicationConfig, loading }) => {
  const classes = useStyles()
  const [loadedCkEditor, setLoadedCkEditor] = useState(false)
  useEffect(() => {
    scriptjs(CkEditorTextbox.config.CDN, () => {
      setLoadedCkEditor(true)
    })
  }, [])

  const onChange = (sanitizedVal) => {
    const updatedState = cloneDeep(applicationConfig)
    const isError = validateInput(sanitizedVal)

    const notesConfig = updatedState.applicationNotes ? updatedState.applicationNotes : {}
    notesConfig.body = sanitizedVal
    updatedState.applicationNotes = { ...notesConfig }

    setApplicationConfig(updatedState, isError)
  }

  const validateInput = (val) => {
    const count = getCharCount(val)
    return count > maxNoteLength
  }

  const initalVal =
    applicationConfig.applicationNotes && applicationConfig.applicationNotes.body
      ? applicationConfig.applicationNotes.body
      : ''

  const isError = validateInput(sanitize(initalVal))

  return (
    <SectionTable
      sectionName="Additional Details"
      onlyShowLabel={true}
    >
      <TableRow>
        {loading ? (
          <TableCell>
            <Skeleton
              data-testid="loader-test-id"
              className={classes.loadingBox}
              height={337}
            />
          </TableCell>
        ) : isEditing ? (
          <TableCell>
            <Typography
              variant="body2"
              className={classes.thirdLevelHeading}
            >
              {additionNoteText}
            </Typography>
            {loadedCkEditor ? (
              <React.Fragment>
                <ErrorBoundary>
                  <CkEditorTextbox
                    initialContent={initalVal}
                    configuration="LINKS"
                    ckEditor={window.CKEDITOR}
                    additionalOptions={{ startupFocus: false, toolbar, versionCheck: false }}
                    onUpdate={(value) => onChange(sanitize(value))}
                  />
                </ErrorBoundary>

                <CharactersRemaining
                  textLength={getCharCount(initalVal)}
                  maxChars={maxNoteLength}
                />

                {
                  /* istanbul ignore next  */ isError && (
                    <Typography className={classes.exceedsError}>Exceeds character count.</Typography>
                  )
                }
              </React.Fragment>
            ) : (
              <Skeleton
                data-testid="loader-test-id"
                className={classes.loadingBox}
                height={337}
              />
            )}
          </TableCell>
        ) : (
          <TableCell>
            <Typography
              variant="body2"
              className={classes.thirdLevelHeading}
            >
              {additionNoteText}
            </Typography>
            {initalVal ? (
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{
                  __html: sanitize(initalVal),
                }}
              ></Typography>
            ) : (
              <Typography
                variant="body2"
                className={classes.secondaryText}
              >
                None
              </Typography>
            )}
          </TableCell>
        )}
      </TableRow>
    </SectionTable>
  )
}

NotesSection.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  applicationConfig: PropTypes.object.isRequired,
  setApplicationConfig: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
}

export default NotesSection
